import { useEffect, useState } from 'react';
import {
  Body1,
  Button,
  Dropdown,
  Field,
  Link,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Spinner,
  Textarea,
  Input,
  tokens,
} from '@fluentui/react-components';
import { BugRegular, LightbulbRegular, SendRegular } from '@fluentui/react-icons';
import { PageHeader } from '../components/PageHeader';
import { useApi } from '../api/useApi';
import { useSession } from '../auth/SessionProvider';
import type { DevConfigDto, DevIssueResultDto } from '../api/types';
import './dev.css';

type IssueType = 'feature' | 'bug';
const SEVERITIES = ['Niedrig', 'Mittel', 'Hoch', 'Kritisch'];

export function DevIssuesPage() {
  const api = useApi();
  const { me } = useSession();

  const [config, setConfig] = useState<DevConfigDto | null>(null);
  const [type, setType] = useState<IssueType>('feature');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('Mittel');
  const [steps, setSteps] = useState('');
  const [expected, setExpected] = useState('');
  const [page, setPage] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<DevIssueResultDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getDevConfig().then(setConfig).catch(() => setConfig({ configured: false, repository: '' }));
  }, [api]);

  function reset() {
    setTitle('');
    setDescription('');
    setSteps('');
    setExpected('');
    setPage('');
    setSeverity('Mittel');
  }

  async function submit() {
    setError(null);
    setCreated(null);
    if (!title.trim()) {
      setError('Bitte einen Titel angeben.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await api.createDevIssue({
        type,
        title: title.trim(),
        description: description.trim() || undefined,
        severity: type === 'bug' ? severity : undefined,
        stepsToReproduce: type === 'bug' ? steps.trim() || undefined : undefined,
        expected: type === 'bug' ? expected.trim() || undefined : undefined,
        page: page.trim() || undefined,
      });
      setCreated(result);
      reset();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  if (me && !me.isAdmin) {
    return (
      <div>
        <PageHeader title="Feature Request & Bugs" />
        <MessageBar intent="warning">
          <MessageBarBody>Dieser Bereich ist M365 Adminbox-Admins vorbehalten.</MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  const cards: { key: IssueType; title: string; sub: string; icon: JSX.Element }[] = [
    { key: 'feature', title: 'Feature Request', sub: 'Eine neue Funktion oder Verbesserung vorschlagen.', icon: <LightbulbRegular /> },
    { key: 'bug', title: 'Bug melden', sub: 'Ein fehlerhaftes Verhalten beschreiben.', icon: <BugRegular /> },
  ];

  return (
    <div className="dev-wrap">
      <PageHeader
        title="Feature Request & Bugs"
        description="Wünsche und Fehler melden — jede Meldung wird direkt als GitHub-Issue angelegt."
      />

      {config && !config.configured ? (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>GitHub-Anbindung noch nicht eingerichtet.</MessageBarTitle>
            Es ist kein Token hinterlegt — Meldungen können daher noch nicht erstellt werden.
          </MessageBarBody>
        </MessageBar>
      ) : null}

      {created ? (
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>Gemeldet — danke!</MessageBarTitle>
            Issue{' '}
            <Link href={created.htmlUrl} target="_blank" rel="noreferrer">
              #{created.number}
            </Link>{' '}
            wurde in GitHub erstellt.
          </MessageBarBody>
        </MessageBar>
      ) : null}

      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}

      {/* Auswahl */}
      <div className="dev-typegrid">
        {cards.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`dev-typecard${type === c.key ? ' selected' : ''}`}
            aria-pressed={type === c.key}
            onClick={() => setType(c.key)}
          >
            <span className="dev-typecard-ic">{c.icon}</span>
            <span>
              <span className="dev-typecard-title" style={{ display: 'block' }}>{c.title}</span>
              <span className="dev-typecard-sub">{c.sub}</span>
            </span>
          </button>
        ))}
      </div>

      {/* Formular */}
      <div className="dev-form">
        <Field label="Titel" required>
          <Input
            value={title}
            onChange={(_, d) => setTitle(d.value)}
            placeholder={type === 'bug' ? 'z. B. LAPS-Passwort wird nicht angezeigt' : 'z. B. CSV-Export für Geräte-Inventar'}
            maxLength={120}
          />
        </Field>

        <Field label={type === 'bug' ? 'Was ist passiert?' : 'Beschreibung'} required>
          <Textarea
            value={description}
            onChange={(_, d) => setDescription(d.value)}
            resize="vertical"
            rows={4}
            placeholder={type === 'bug' ? 'Beschreibe das Problem so konkret wie möglich.' : 'Was soll die Funktion können — und warum ist sie hilfreich?'}
          />
        </Field>

        {type === 'bug' ? (
          <>
            <Field label="Schweregrad">
              <Dropdown
                value={severity}
                selectedOptions={[severity]}
                onOptionSelect={(_, d) => d.optionValue && setSeverity(d.optionValue)}
              >
                {SEVERITIES.map((s) => (
                  <Option key={s} value={s}>{s}</Option>
                ))}
              </Dropdown>
            </Field>
            <Field label="Schritte zur Reproduktion">
              <Textarea
                value={steps}
                onChange={(_, d) => setSteps(d.value)}
                resize="vertical"
                rows={3}
                placeholder={'1. …\n2. …\n3. …'}
              />
            </Field>
            <Field label="Erwartetes Verhalten">
              <Textarea value={expected} onChange={(_, d) => setExpected(d.value)} resize="vertical" rows={2} />
            </Field>
          </>
        ) : null}

        <Field label={type === 'bug' ? 'Betroffene Seite (optional)' : 'Bereich (optional)'}>
          <Input value={page} onChange={(_, d) => setPage(d.value)} placeholder="z. B. Geräte → LAPS" />
        </Field>

        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
          <Button
            appearance="primary"
            icon={submitting ? <Spinner size="tiny" /> : <SendRegular />}
            disabled={submitting || !title.trim() || (config != null && !config.configured)}
            onClick={submit}
          >
            {submitting ? 'Wird gemeldet…' : 'Als GitHub-Issue melden'}
          </Button>
          {config?.configured ? (
            <Body1 style={{ color: tokens.colorNeutralForeground3 }}>→ {config.repository}</Body1>
          ) : null}
        </div>
      </div>
    </div>
  );
}
